import { useMemo, useState } from 'react';
import {
  canChooseSegments,
  chosenSegmentIds,
  priorSegmentsFromLetter,
  type PriorSegment,
} from '@/lib/medcom/priorSegments';

type ForwardLetter = Parameters<typeof priorSegmentsFromLetter>[0];

export type ForwardChoice = {
  /** True when the letter's parts can be picked one by one. */
  selectable: boolean;
  isChosen: (key: string) => boolean;
  toggle: (key: string) => void;
  /** Sent as forwardSegmentIds. Undefined forwards the whole letter. */
  segmentIds: string[] | undefined;
  /** Every part is unticked, so there is nothing to forward. */
  nothingChosen: boolean;
};

/**
 * Which parts of the letter being forwarded are carried along.
 * Everything is chosen until the user unticks a part.
 */
export function useForwardChoice(
  letter: ForwardLetter | null | undefined,
  letterKey: string | null,
  enabled: boolean,
): ForwardChoice {
  const segments = useMemo<PriorSegment[]>(
    () => (enabled && letter ? priorSegmentsFromLetter(letter) : []),
    [enabled, letter],
  );
  const [state, setState] = useState<{ letterKey: string | null; leftOut: ReadonlySet<string> }>({
    letterKey,
    leftOut: new Set(),
  });
  const leftOut = state.letterKey === letterKey ? state.leftOut : new Set<string>();

  const selectable = canChooseSegments(segments);
  const segmentIds = chosenSegmentIds(segments, leftOut);

  return {
    selectable,
    isChosen: (key) => !leftOut.has(key),
    toggle: (key) => {
      const next = new Set(leftOut);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      setState({ letterKey, leftOut: next });
    },
    segmentIds,
    nothingChosen: selectable && segmentIds?.length === 0,
  };
}
